/* ==========================================================================
   KIVORA — global.js
   Dependency-free interaction engine. Each concern is an isolated module with
   an init() that no-ops when its target nodes are absent, so sections can be
   added or removed in the Customizer without breaking anything.
   ========================================================================== */
(function () {
  'use strict';

  const K = window.KIVORA || {};
  const doc = document;
  const root = doc.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animationsOn = (K.settings && K.settings.animations) && !reduceMotion;

  /* ---- helpers ---------------------------------------------------------- */
  const $ = (sel, ctx = doc) => ctx.querySelector(sel);
  const $$ = (sel, ctx = doc) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, ev, fn, opt) => el && el.addEventListener(ev, fn, opt);
  const raf = window.requestAnimationFrame.bind(window);

  /* boot() re-runs on every shopify:section:load. Elements that survive a
     section reload must not collect the same listener twice. */
  const claim = (el) => {
    if (!el || el.dataset.kvBound) return false;
    el.dataset.kvBound = '1';
    return true;
  };

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || K.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    let value = '';
    const match = fmt.match(placeholder);
    switch (match && match[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  /* Storage access that can never throw.
     In the theme editor the storefront is a cross-origin iframe, and browsers
     with strict cookie isolation (Firefox Total Cookie Protection, Safari ITP)
     raise a SecurityError on localStorage/sessionStorage there. Private mode
     and disabled cookies do the same. Every access goes through here. */
  const storage = {
    get(kind, key) {
      try { return window[kind + 'Storage'].getItem(key); } catch (e) { return null; }
    },
    set(kind, key, value) {
      try { window[kind + 'Storage'].setItem(key, value); } catch (e) { /* unavailable */ }
    }
  };

  const escapeHtml = (str) => String(str == null ? '' : str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  /* ---- 01 · Loading screen ---------------------------------------------
     Storage is accessed through the guarded helpers above. Inside the theme
     editor the storefront runs in a cross-origin iframe, where browsers with
     strict cookie isolation throw on any storage access — an unguarded call
     here would abort the whole boot sequence and leave this overlay covering
     the page for good. */
  const LoadingScreen = {
    init() {
      const el = $('[data-loading-screen]');
      if (!el) return;

      const remove = () => {
        el.classList.add('is-done');
        setTimeout(() => el.remove(), 900);
      };

      if (!animationsOn || !(K.settings && K.settings.loadingScreen) || storage.get('session', 'kv_loaded')) {
        el.classList.add('is-done');
        el.remove();
        return;
      }
      const dismiss = () => {
        storage.set('session', 'kv_loaded', '1');
        remove();
      };
      window.addEventListener('load', () => setTimeout(dismiss, 1100));
      setTimeout(dismiss, 2600); // safety net
    }
  };

  /* ---- 02 · Scroll reveal ---------------------------------------------- */
  const Reveal = {
    init() {
      /* .media-reveal is styled by the same arm-then-observe contract but
         carries no data-reveal attribute, so it was never observed and its
         images stayed masked. Both selectors belong to one system. */
      const targets = $$('[data-reveal], .media-reveal');
      if (!targets.length) return;
      if (!animationsOn) { targets.forEach((t) => t.classList.add('is-visible')); return; }

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = el.dataset.revealDelay;
          if (delay) el.style.setProperty('--reveal-delay', `${parseInt(delay, 10)}ms`);
          el.classList.add('is-visible');
          io.unobserve(el);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });

      // Arm first, then observe: an element only hides once JS is definitely running.
      targets.forEach((t) => { t.classList.add('is-armed'); io.observe(t); });
    }
  };

  /* ---- 03 · Split text into line reveals -------------------------------- */
  const SplitText = {
    init() {
      /* Ohne diesen Rueckfall bleibt die Ueberschrift fuer immer weg: Die Zeilen
         starten per CSS auf translateY(110%) hinter overflow:hidden und werden
         erst durch .is-visible hereingefahren. Wer "Bewegung reduzieren"
         aktiviert hat, sah bisher eine leere Flaeche statt der Schlagzeile.
         Reveal macht es an anderer Stelle bereits richtig. */
      if (!animationsOn) {
        $$('[data-split]').forEach((el) => el.classList.add('is-visible'));
        return;
      }
      $$('[data-split]').forEach((el) => {
        const io = new IntersectionObserver((entries, obs) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            e.target.classList.add('is-visible');
            obs.unobserve(e.target);
          });
        }, { threshold: 0.2 });
        // Wrap existing markup line-by-line only if authored with <span> lines
        $$('.reveal-line > span', el).forEach((span, i) => {
          span.style.setProperty('--line-delay', `${i * 90}ms`);
        });
        io.observe(el);
      });
    }
  };

  /* ---- 04 · Header behaviour ------------------------------------------- */
  const Header = {
    init() {
      const header = $('[data-header]');
      if (!header || !claim(header)) return;
      const isOverlay = header.classList.contains('header--overlay');
      const solidAt = 40;
      let last = 0;

      const update = () => {
        const y = window.scrollY;
        if (isOverlay) header.classList.toggle('is-solid', y > solidAt);
        // Hide-on-scroll-down for solid headers only, once past the hero
        const persistent = header.hasAttribute('data-header-persistent');
        if (!persistent && y > 400 && y > last && !header.matches(':focus-within')) {
          header.classList.add('is-hidden');
        } else {
          header.classList.remove('is-hidden');
        }
        last = y;
      };
      update();
      window.addEventListener('scroll', () => raf(update), { passive: true });
    }
  };

  /* ---- 04b · Navigation panel ------------------------------------------
     Hover and keyboard focus are handled entirely in CSS. This module exists
     only for touch, where there is no hover: the first tap opens the panel,
     the second follows the link. Without it a phone user would be sent to the
     parent page before ever seeing the collections. */
  const NavPanel = {
    init() {
      const groups = $$('[data-nav-group]');
      if (!groups.length) return;
      if (!window.matchMedia('(hover: none)').matches) return;

      groups.forEach((group) => {
        const trigger = $('[data-nav-trigger]', group);
        if (!trigger || !claim(trigger)) return;

        on(trigger, 'click', (e) => {
          if (group.classList.contains('is-open')) return; // second tap follows the link
          e.preventDefault();
          groups.forEach((g) => g !== group && g.classList.remove('is-open'));
          group.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        });
      });

      // A tap anywhere else closes whatever is open.
      on(doc, 'click', (e) => {
        if (e.target.closest('[data-nav-group]')) return;
        groups.forEach((g) => {
          g.classList.remove('is-open');
          const t = $('[data-nav-trigger]', g);
          t && t.setAttribute('aria-expanded', 'false');
        });
      });
    }
  };

  /* ---- 05 · Mobile menu ------------------------------------------------- */
  const MobileMenu = {
    init() {
      const toggle = $('[data-menu-toggle]');
      const drawer = $('[data-mobile-menu]');
      const overlay = $('[data-mobile-menu-overlay]');
      if (!toggle || !drawer || !claim(toggle)) return;

      const open = () => {
        drawer.classList.add('is-open');
        overlay && overlay.classList.add('is-active');
        toggle.setAttribute('aria-expanded', 'true');
        root.classList.add('no-scroll');
        const first = $('a, button', drawer);
        first && first.focus();
      };
      const close = () => {
        drawer.classList.remove('is-open');
        overlay && overlay.classList.remove('is-active');
        toggle.setAttribute('aria-expanded', 'false');
        root.classList.remove('no-scroll');
      };
      on(toggle, 'click', () => (drawer.classList.contains('is-open') ? close() : open()));
      on(overlay, 'click', close);
      $$('[data-menu-close]', drawer).forEach((b) => on(b, 'click', close));
      on(doc, 'keydown', (e) => e.key === 'Escape' && drawer.classList.contains('is-open') && close());

      // Nested accordions inside mobile menu
      $$('[data-submenu-toggle]', drawer).forEach((btn) => {
        on(btn, 'click', () => {
          const item = btn.closest('[data-submenu]');
          item && item.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', item && item.classList.contains('is-open') ? 'true' : 'false');
        });
      });
    }
  };

  /* ---- 06 · Accordions -------------------------------------------------- */
  const Accordion = {
    init() {
      $$('[data-accordion]').forEach((acc) => {
        const btn = $('[data-accordion-trigger]', acc);
        const panel = $('[data-accordion-panel]', acc);
        if (!btn || !panel || !claim(btn)) return;
        const setOpen = (open) => {
          acc.classList.toggle('is-open', open);
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          panel.style.maxHeight = open ? `${panel.scrollHeight}px` : '0px';
        };
        setOpen(acc.hasAttribute('data-open'));
        on(btn, 'click', () => setOpen(!acc.classList.contains('is-open')));
        window.addEventListener('resize', () => {
          if (acc.classList.contains('is-open')) panel.style.maxHeight = `${panel.scrollHeight}px`;
        }, { passive: true });
      });
    }
  };

  /* ---- 06b · Produktbeschreibung gliedern -------------------------------
     Die Beschreibung kommt als ein Block HTML aus dem Produkt. Alles offen
     nebeneinander ueberfordert. Jede <h3>-Ueberschrift samt Text bis zur
     naechsten wird in ein Akkordeon verwandelt; der Text vor der ersten
     Ueberschrift bleibt als Kurzbeschreibung stehen. Vorteil gegenueber fest
     gebauten Bereichen: Jedes Produkt bringt seine eigenen Abschnitte mit. */
  const Beschreibung = {
    init() {
      $$('[data-desc-accordion]').forEach((wrap) => {
        if (wrap.dataset.kvGegliedert) return;
        const kinder = Array.from(wrap.children);
        if (!kinder.some((el) => el.tagName === 'H3')) return;
        const intro = doc.createElement('div');
        intro.className = 'pdp__desc-intro';
        const gruppen = [];
        let aktuell = null;
        kinder.forEach((el) => {
          if (el.tagName === 'H3') {
            aktuell = { titel: el.textContent.trim(), inhalt: [] };
            gruppen.push(aktuell);
          } else if (aktuell) {
            aktuell.inhalt.push(el);
          } else {
            intro.appendChild(el);
          }
        });
        wrap.textContent = '';
        if (intro.children.length) wrap.appendChild(intro);
        gruppen.forEach((g, i) => {
          const acc = doc.createElement('div');
          acc.className = 'pdp__accordion accordion';
          acc.setAttribute('data-accordion', '');
          const btn = doc.createElement('button');
          btn.className = 'accordion__trigger';
          btn.type = 'button';
          btn.setAttribute('data-accordion-trigger', '');
          btn.setAttribute('aria-expanded', 'false');
          const label = doc.createElement('span');
          label.textContent = g.titel;
          const icon = doc.createElement('span');
          icon.className = 'accordion__icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = '+';
          btn.appendChild(label);
          btn.appendChild(icon);
          const panel = doc.createElement('div');
          panel.className = 'accordion__panel';
          panel.setAttribute('data-accordion-panel', '');
          const inner = doc.createElement('div');
          inner.className = 'rte accordion__content';
          g.inhalt.forEach((el) => inner.appendChild(el));
          panel.appendChild(inner);
          acc.appendChild(btn);
          acc.appendChild(panel);
          if (i === 0) acc.setAttribute('data-open', '');
          wrap.appendChild(acc);
        });
        wrap.dataset.kvGegliedert = '1';
      });
    }
  };

  /* ---- 07 · Magnetic buttons ------------------------------------------- */
  const Magnetic = {
    init() {
      if (!animationsOn || !(K.settings && K.settings.magneticButtons)) return;
      if (window.matchMedia('(pointer: coarse)').matches) return;
      $$('[data-magnetic]').forEach((el) => {
        if (!claim(el)) return;
        const strength = parseFloat(el.dataset.magnetic) || 0.3;
        on(el, 'mousemove', (e) => {
          const r = el.getBoundingClientRect();
          const x = (e.clientX - r.left - r.width / 2) * strength;
          const y = (e.clientY - r.top - r.height / 2) * strength;
          el.style.setProperty('--mx', `${x}px`);
          el.style.setProperty('--my', `${y}px`);
        });
        on(el, 'mouseleave', () => {
          el.style.setProperty('--mx', '0px');
          el.style.setProperty('--my', '0px');
        });
      });
    }
  };

  /* ---- 08 · Custom cursor ---------------------------------------------- */
  const Cursor = {
    init() {
      const cursor = $('[data-cursor]');
      if (!cursor || !claim(cursor) || !(K.settings && K.settings.customCursor)) return;
      if (window.matchMedia('(pointer: coarse)').matches || reduceMotion) { cursor.remove(); return; }
      let x = 0, y = 0, cx = 0, cy = 0;
      on(window, 'mousemove', (e) => {
        x = e.clientX; y = e.clientY;
        cursor.classList.add('is-active');
      });
      const loop = () => {
        cx += (x - cx) * 0.18;
        cy += (y - cy) * 0.18;
        cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0) translate(-50%, -50%)`;
        raf(loop);
      };
      raf(loop);
      const hoverSel = 'a, button, [data-magnetic], .card, input, textarea';
      on(doc, 'mouseover', (e) => e.target.closest(hoverSel) && cursor.classList.add('is-hover'));
      on(doc, 'mouseout', (e) => e.target.closest(hoverSel) && cursor.classList.remove('is-hover'));
      on(doc, 'mouseleave', () => cursor.classList.remove('is-active'));
    }
  };

  /* ---- 09 · Parallax ---------------------------------------------------- */
  const Parallax = {
    listening: false,
    init() {
      if (!animationsOn) return;
      const update = () => {
        const items = $$('[data-parallax]');
        if (!items.length) return;
        const vh = window.innerHeight;
        items.forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh) return;
          const speed = parseFloat(el.dataset.parallax) || 0.15;
          const shift = (r.top + r.height / 2 - vh / 2) * -speed;
          el.style.setProperty('--parallax', `${shift.toFixed(1)}px`);
        });
      };
      update();
      if (this.listening) return;
      this.listening = true;
      window.addEventListener('scroll', () => raf(update), { passive: true });
      window.addEventListener('resize', () => raf(update), { passive: true });
    }
  };

  /* ---- 09b · Hero film --------------------------------------------------
     The markup already autoplays; this only takes things away again.

     Three reasons to stop the film, all of them the visitor's, none of them
     ours: they asked their system for less motion, they have scrolled past it,
     or the browser refused autoplay outright (saver mode, iOS low power). In
     every case the poster frame remains — the panel was a photograph before it
     was a film, so there is nothing to fall back to and nothing to repair. */
  const HeroVideo = {
    init() {
      const films = $$('[data-hero-video]');
      if (!films.length) return;

      const still = !animationsOn;

      films.forEach((film) => {
        if (still) {
          film.removeAttribute('autoplay');
          film.pause();
          return;
        }

        /* A refused play() rejects a promise. Unhandled, it reaches the console
           as an error on a perfectly healthy page. */
        const play = () => {
          const attempt = film.play();
          if (attempt && attempt.catch) attempt.catch(() => {});
        };

        if (!('IntersectionObserver' in window)) { play(); return; }

        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) play();
            else film.pause();
          });
        }, { threshold: 0.15 });
        io.observe(film);
      });

      /* Someone changing the setting mid-visit should see it take effect. */
      const query = window.matchMedia('(prefers-reduced-motion: reduce)');
      const listen = query.addEventListener ? 'addEventListener' : null;
      if (listen) {
        query[listen]('change', (e) => {
          films.forEach((film) => {
            if (e.matches) film.pause();
            else { const a = film.play(); if (a && a.catch) a.catch(() => {}); }
          });
        });
      }
    }
  };

  /* ---- 10 · Marquee: duration from content width + a11y pause ----------
     WCAG 2.2.2 requires a mechanism to pause motion that starts automatically
     and runs longer than five seconds. Hover alone does not serve keyboard or
     touch users, so a focusable control is injected: invisible until focused,
     leaving the visual design untouched. */
  const Marquee = {
    init() {
      $$('[data-marquee]').forEach((track) => {
        const speed = parseFloat(track.dataset.marquee) || 60; // px per second
        const width = track.scrollWidth / 2;
        if (width > 0) {
          track.style.setProperty('--marquee-duration', `${(width / speed).toFixed(1)}s`);
        }
        if (!claim(track)) return;

        const btn = doc.createElement('button');
        btn.type = 'button';
        btn.className = 'motion-pause';
        btn.textContent = (K.strings && K.strings.pauseMotion) || 'Bewegung pausieren';
        btn.setAttribute('aria-pressed', 'false');

        on(btn, 'click', () => {
          const paused = track.classList.toggle('is-paused');
          btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
          btn.textContent = paused
            ? (K.strings && K.strings.resumeMotion) || 'Bewegung fortsetzen'
            : (K.strings && K.strings.pauseMotion) || 'Bewegung pausieren';
        });

        track.parentNode.insertBefore(btn, track);
      });
    }
  };

  /* ---- 11 · Quantity steppers ------------------------------------------ */
  const Quantity = {
    init() {
      $$('[data-qty]').forEach((wrap) => {
        const input = $('input', wrap);
        if (!input) return;
        $$('[data-qty-btn]', wrap).forEach((btn) => {
          if (!claim(btn)) return;
          on(btn, 'click', () => {
            const step = btn.dataset.qtyBtn === 'plus' ? 1 : -1;
            const min = parseInt(input.min, 10) || 1;
            const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
            input.value = next;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      });
    }
  };

  /* ---- 12 · Cart -------------------------------------------------------- */
  const Cart = {
    els: {},
    bound: false,
    init() {
      this.els.overlay = $('[data-cart-overlay]');
      this.els.drawer = $('[data-cart-drawer]');
      this.els.body = $('[data-cart-body]');
      this.els.count = $$('[data-cart-count]');
      this.bind();
      this.refresh();
    },
    bind() {
      // Element-level handlers: safe to re-bind, the nodes are replaced on
      // section reload. Guarded against duplicates via a marker attribute.
      $$('[data-cart-open]').forEach((b) => {
        if (b.dataset.kvBound) return;
        b.dataset.kvBound = '1';
        on(b, 'click', (e) => { e.preventDefault(); this.open(); });
      });
      $$('[data-cart-close]').forEach((b) => {
        if (b.dataset.kvBound) return;
        b.dataset.kvBound = '1';
        on(b, 'click', () => this.close());
      });

      // Document-level delegation must bind exactly once — boot() re-runs on
      // every shopify:section:load, and duplicates would submit twice.
      if (this.bound) return;
      this.bound = true;

      on(this.els.overlay, 'click', () => this.close());
      on(doc, 'keydown', (e) => e.key === 'Escape' && this.isOpen() && this.close());

      on(doc, 'submit', (e) => {
        const form = e.target.closest('[data-product-form]');
        if (!form) return;
        e.preventDefault();
        this.add(form);
      });

      on(doc, 'click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.change(remove.dataset.cartRemove, 0); }
      });
      on(doc, 'change', (e) => {
        const qty = e.target.closest('[data-cart-qty-input]');
        if (qty) this.change(qty.dataset.cartQtyInput, parseInt(qty.value, 10) || 0);
      });
    },
    isOpen() { return this.els.drawer && this.els.drawer.classList.contains('is-open'); },
    open() {
      if (!this.els.drawer) return;
      this.els.drawer.classList.add('is-open');
      this.els.overlay && this.els.overlay.classList.add('is-active');
      this.els.drawer.setAttribute('aria-hidden', 'false');
      root.classList.add('no-scroll');
      const close = $('[data-cart-close]', this.els.drawer);
      close && close.focus();
    },
    close() {
      if (!this.els.drawer) return;
      this.els.drawer.classList.remove('is-open');
      this.els.overlay && this.els.overlay.classList.remove('is-active');
      this.els.drawer.setAttribute('aria-hidden', 'true');
      root.classList.remove('no-scroll');
    },
    async add(form) {
      const btn = $('[data-add-btn]', form);
      const label = btn ? $('[data-add-label]', btn) : null;
      const original = label ? label.textContent : '';
      if (btn) { btn.setAttribute('aria-disabled', 'true'); }
      if (label) label.textContent = '···';
      try {
        const res = await fetch(`${K.routes.cart_add}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(this.formToJSON(form))
        });
        if (!res.ok) throw new Error('add_failed');
        if (label) label.textContent = (K.strings && K.strings.added) || original;
        await this.refresh(true);
        this.open();
        setTimeout(() => { if (label) label.textContent = original; }, 1600);
      } catch (err) {
        if (label) label.textContent = (K.strings && K.strings.cartError) || 'Error';
        setTimeout(() => { if (label) label.textContent = original; }, 1800);
      } finally {
        if (btn) btn.removeAttribute('aria-disabled');
      }
    },
    formToJSON(form) {
      const id = form.querySelector('[name="id"]').value;
      const qtyEl = form.querySelector('[name="quantity"]');
      return { id, quantity: qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1 };
    },
    async change(key, quantity) {
      try {
        const res = await fetch(`${K.routes.cart_change}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await res.json();
        this.render(cart);
      } catch (err) { /* silent — cart stays as-is */ }
    },
    async refresh(silent) {
      try {
        const res = await fetch(`${K.routes.cart}.js`);
        const cart = await res.json();
        this.render(cart);
      } catch (err) { /* offline */ }
    },
    render(cart) {
      this.els.count.forEach((c) => {
        c.textContent = cart.item_count;
        c.toggleAttribute('hidden', cart.item_count === 0);
      });
      if (!this.els.body) return;

      if (cart.item_count === 0) {
        this.els.body.innerHTML = `
          <div class="cart-empty">
            <p class="h3">${escapeHtml((K.strings && K.strings.emptyTitle) || 'Ihr Warenkorb ist noch leer.')}</p>
            <a href="/collections/all" class="btn" data-cart-close>${escapeHtml((K.strings && K.strings.continueShopping) || 'Weiter einkaufen')}</a>
          </div>`;
        const footer = $('[data-cart-footer]');
        if (footer) footer.hidden = true;
        return;
      }

      const items = cart.items.map((item) => {
        const img = item.image
          ? `<img src="${item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_200x$1$2')}" alt="${escapeHtml(item.product_title)}" loading="lazy" width="84" height="112">`
          : '';
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<span class="cart-item__variant">${escapeHtml(item.variant_title)}</span>` : '';
        const sellingPlan = item.selling_plan_allocation
          ? `<span class="cart-item__variant">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</span>` : '';
        return `
          <article class="cart-item">
            <a class="cart-item__media" href="${item.url}">${img}</a>
            <div class="cart-item__info">
              <a class="cart-item__title" href="${item.url}">${escapeHtml(item.product_title)}</a>
              ${variant}${sellingPlan}
              <div class="cart-item__row">
                <div class="qty" data-qty>
                  <button class="qty__btn" type="button" data-qty-btn="minus" aria-label="Decrease">–</button>
                  <input class="qty__input" type="number" min="0" value="${item.quantity}" data-cart-qty-input="${item.key}" aria-label="Quantity">
                  <button class="qty__btn" type="button" data-qty-btn="plus" aria-label="Increase">+</button>
                </div>
                <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
              </div>
              <button class="cart-item__remove" type="button" data-cart-remove="${item.key}">${escapeHtml((K.strings && K.strings.remove) || 'Remove')}</button>
            </div>
          </article>`;
      }).join('');

      this.els.body.innerHTML = items;
      // re-bind quantity steppers created above
      Quantity.init();

      // Footer: totals + free-shipping progress
      const footer = $('[data-cart-footer]');
      if (footer) {
        footer.hidden = false;
        const totalEl = $('[data-cart-total]', footer);
        if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
        const bar = $('[data-ship-bar]', footer);
        if (bar) {
          const threshold = parseInt(bar.dataset.shipBar, 10) || 0;
          if (threshold > 0) {
            const remaining = threshold - cart.total_price;
            const fill = $('[data-ship-fill]', bar);
            const label = $('[data-ship-label]', bar);
            const pct = Math.min(100, (cart.total_price / threshold) * 100);
            if (fill) fill.style.width = `${pct}%`;
            if (label) {
              label.textContent = remaining > 0
                ? (K.strings.shipRemaining || 'Noch [amount] bis zum kostenlosen Versand.').replace('[amount]', formatMoney(remaining))
                : (K.strings.shipQualified || 'Sie erhalten kostenlosen Versand.');
            }
            bar.hidden = false;
          } else { bar.hidden = true; }
        }
      }
    }
  };

  /* ---- 13 · Product page: variants, gallery, sticky bar ---------------- */
  const Product = {
    init() {
      this.variants();
      this.gallery();
      this.stickyBar();
    },

    /* Variant picker — keeps URL, price, availability and every hidden
       variant input in sync without a page reload. */
    variants() {
      const picker = $('[data-variant-picker]');
      const dataEl = $('[data-variant-data]');
      if (!picker || !dataEl || !claim(picker)) return;

      let variants = [];
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      const priceWrap = $('[data-price-wrap]');
      const idInputs = $$('[data-variant-id]');
      const addBtns = $$('[data-add-btn]');

      /* Mengenvorteil. Die Werte kommen fertig aus Liquid, damit hier nichts
         nachgerechnet wird, was serverseitig schon feststeht. Format je Variante:
         [gespartInCent, vergleichspreisInCent, prozent, anzahl2erSets] */
      const savingsEl = $('[data-savings]');
      const savingsDataEl = $('[data-savings-data]');
      let savings = {};
      if (savingsDataEl) {
        try { savings = JSON.parse(savingsDataEl.textContent); } catch (e) { savings = {}; }
      }

      const selected = () => $$('.pdp__radio:checked', picker)
        .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
        .map((i) => i.value);

      const update = () => {
        const chosen = selected();
        const match = variants.find((v) =>
          v.options.every((opt, i) => opt === chosen[i])
        );

        // Reflect the chosen value next to each option label
        $$('[data-option-value]', picker).forEach((el, i) => {
          if (chosen[i]) el.textContent = chosen[i];
        });

        if (!match) {
          addBtns.forEach((b) => {
            b.disabled = true;
            const l = $('[data-add-label]', b);
            if (l) l.textContent = (K.strings && K.strings.unavailable) || 'Unavailable';
          });
          return;
        }

        idInputs.forEach((input) => { input.value = match.id; });
        $$('[data-variant-sku]').forEach((el) => { el.textContent = match.sku || '–'; });

        if (priceWrap) {
          const price = formatMoney(match.price);
          const compare = match.compare_at_price && match.compare_at_price > match.price
            ? `<s class="price__compare">${formatMoney(match.compare_at_price)}</s> <span class="price--sale">${price}</span>`
            : price;
          const priceEl = $('.price', priceWrap);
          if (priceEl) priceEl.innerHTML = compare;
        }

        if (savingsEl) {
          const eintrag = savings[match.id];
          if (eintrag) {
            const [gespart, vergleich, prozent, paare] = eintrag;
            const betragEl = $('[data-savings-amount]', savingsEl);
            const refEl = $('[data-savings-ref]', savingsEl);
            if (betragEl) betragEl.textContent = `Sie sparen ${formatMoney(gespart)} (${prozent} %)`;
            if (refEl) {
              refEl.textContent = `statt ${formatMoney(vergleich)} als ${paare} × 2er-Set`;
            }
            savingsEl.hidden = false;
          } else {
            savingsEl.hidden = true;
          }
        }

        addBtns.forEach((b) => {
          b.disabled = !match.available;
          const l = $('[data-add-label]', b);
          if (!l) return;
          l.textContent = match.available
            ? (K.strings && K.strings.addToCart) || 'In den Warenkorb'
            : (K.strings && K.strings.soldOut) || 'Ausverkauft';
        });

        /* Preis an jeder Auswahl. Fuer jede Option wird die Variante gesucht,
           die entstuende, wenn man genau diesen Wert waehlt und die uebrigen
           Optionen so laesst wie sie sind. Dadurch sieht der Besucher den
           Mengenvorteil, bevor er klickt - und muss nicht jede Kombination
           durchprobieren. Der Stueckpreis erscheint nur dort, wo eine
           Stueckzahl im Wert steht. */
        $$('.pdp__radio', picker).forEach((radio) => {
          const label = picker.querySelector(`label[for="${radio.id}"]`);
          const ziel = label && label.querySelector('[data-pill-price]');
          if (!ziel) return;
          const idx = Number(radio.dataset.optionIndex);
          const probe = chosen.slice();
          probe[idx] = radio.value;
          const treffer = variants.find((v) => v.options.every((o, i) => o === probe[i]));
          if (!treffer || !treffer.available) {
            ziel.textContent = treffer ? '' : '–';
            label.classList.toggle('is-unavailable', !treffer);
            return;
          }
          label.classList.remove('is-unavailable');
          const anzahl = Number(label.dataset.count || 0);
          ziel.textContent = anzahl > 1
            ? `${formatMoney(Math.round(treffer.price / anzahl))} pro Stück`
            : formatMoney(treffer.price);
        });

        // Shareable URL without a reload
        if (window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url.toString());
        }
      };

      on(picker, 'change', (e) => {
        if (e.target.classList.contains('pdp__radio')) update();
      });
      update();
    },

    /* Gallery — thumbnails scroll the matching frame into view and stay
       highlighted as the reader scrolls the column. */
    gallery() {
      const frames = $$('[data-gallery-item]');
      const thumbs = $$('[data-gallery-thumb]');
      if (!frames.length || !thumbs.length) return;

      thumbs.forEach((thumb) => {
        on(thumb, 'click', (e) => {
          e.preventDefault();
          const idx = thumb.dataset.galleryThumb;
          const target = frames.find((f) => f.dataset.galleryItem === idx);
          if (target) target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center', inline: 'center' });
        });
      });

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = entry.target.dataset.galleryItem;
          thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.galleryThumb === idx));
        });
      }, { threshold: 0.55 });
      frames.forEach((f) => io.observe(f));
    },

    /* Sticky bar appears once the primary buy button has scrolled away. */
    stickyBar() {
      const bar = $('[data-sticky-atc]');
      const anchor = $('.pdp__buy');
      if (!bar || !anchor) return;

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      }, { threshold: 0 });
      io.observe(anchor);
    }
  };

  /* ---- 14 · Cookie preferences (Shopify Customer Privacy API) -----------
     Shopify owns the consent banner; the theme only offers a way back into it.
     The control stays hidden unless the API is genuinely available, so shops
     without a banner never show a link that does nothing. */
  const CookiePreferences = {
    init() {
      const holder = $('[data-cookie-preferences]');
      if (!holder || !claim(holder)) return;

      /* Shopify laedt die Privacy-API asynchron. Frueher wurde nur einmal beim
         Seitenaufbau geprueft – die API war da oft noch nicht geladen, und der
         Link blieb dauerhaft versteckt. Jetzt: API aktiv anfordern und kurz
         nachpruefen. Der Link erscheint erst, wenn er wirklich funktioniert. */
      let shown = false;
      const reveal = () => {
        if (shown) return true;
        const api = window.Shopify && window.Shopify.customerPrivacy;
        if (!api || typeof api.showPreferences !== 'function') return false;
        shown = true;
        holder.hidden = false;
        const btn = $('button', holder);
        on(btn, 'click', () => {
          try { api.showPreferences(); } catch (e) { /* banner not active */ }
        });
        return true;
      };
      if (reveal()) return;

      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        try {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            (err) => { if (!err) reveal(); }
          );
        } catch (e) { /* fall through to polling */ }
      }
      let tries = 0;
      const timer = setInterval(() => {
        if (reveal() || ++tries > 20) clearInterval(timer);
      }, 500);
    }
  };

  /* ---- 15 · Welcome offer ----------------------------------------------
     An invitation on a timer, shown once. It never locks the page, so it stays
     a non-modal dialog: focus moves in only when it opens, Escape closes it,
     and the visitor can keep scrolling underneath. */
  const WelcomeOffer = {
    init() {
      const el = $('[data-welcome-offer]');
      if (!el || !claim(el)) return;

      const key = el.dataset.storageKey;
      const remindDays = parseInt(el.dataset.remindDays, 10) || 0;

      // In the theme editor the timer is suppressed entirely — an invitation
      // reappearing on every reload makes the editor unusable. Instead it opens
      // when the merchant selects the section in the sidebar, which is the
      // convention Shopify sets for popup sections.
      const inEditor = !!(window.Shopify && window.Shopify.designMode);
      if (inEditor) {
        const sectionEl = el.closest('.shopify-section') || el;
        on(doc, 'shopify:section:select', (e) => {
          if (e.target === sectionEl || sectionEl.contains(e.target)) this.open(el);
        });
        on(doc, 'shopify:section:deselect', (e) => {
          if (e.target === sectionEl || sectionEl.contains(e.target)) this.close(el, null);
        });
        return;
      }

      // Returning from Shopify's form redirect: show the success state at once
      // and never ask this visitor again. The marker is rendered by Liquid
      // inside the form block, the only scope where the result is known.
      if ($('[data-welcome-posted]', el)) {
        this.store(key, 'subscribed');
        this.open(el, true);
        return;
      }

      if (!this.shouldShow(key, remindDays)) return;

      let opened = false;
      const timers = [];
      const reveal = () => {
        if (opened) return;
        opened = true;
        timers.forEach(clearTimeout);
        this.open(el);
      };

      const delay = (parseInt(el.dataset.delay, 10) || 30) * 1000;
      timers.push(setTimeout(reveal, delay));

      // Exit intent: pointer leaves through the top of the viewport. Desktop
      // only — on touch there is no cursor to read, and guessing would fire it
      // at random moments.
      if (el.dataset.exitIntent === 'true' && !window.matchMedia('(pointer: coarse)').matches) {
        const onLeave = (e) => {
          if (e.clientY > 0 || e.relatedTarget) return;
          doc.removeEventListener('mouseout', onLeave);
          reveal();
        };
        // Held back briefly so a stray cursor movement on arrival cannot trigger it
        timers.push(setTimeout(() => doc.addEventListener('mouseout', onLeave), 8000));
      }

      $$('[data-welcome-close]', el).forEach((b) =>
        on(b, 'click', () => this.close(el, key))
      );
      on(doc, 'keydown', (e) => {
        if (e.key === 'Escape' && el.classList.contains('is-open')) this.close(el, key);
      });
    },

    /* Two different memories, on purpose:
       - A dismissal lives in sessionStorage: it lasts for this visit and is
         gone once the browser closes, so a returning visitor is greeted again
         rather than silently ignored forever.
       - A subscription lives in localStorage: someone who already signed up
         must never be asked a second time, on any later visit. */
    shouldShow(key, remindDays) {
      if (storage.get('local', key) === 'subscribed') return false;

      const raw = storage.get('session', key);
      if (!raw) return true;

      const dismissedAt = parseInt(raw, 10);
      if (!dismissedAt) return false;
      if (remindDays <= 0) return false;
      return Date.now() - dismissedAt > remindDays * 864e5;
    },
    store(key, value) {
      if (value === 'subscribed') storage.set('local', key, value);
      else storage.set('session', key, value);
    },

    open(el, showSuccess) {
      el.hidden = false;
      // Next frame so the transition has a start state to animate from
      raf(() => raf(() => el.classList.add('is-open')));

      const focusTarget = showSuccess
        ? $('[data-welcome-close]', el)
        : ($('input[type="email"]', el) || $('[data-welcome-close]', el));
      // Focus without yanking the page around the visitor
      if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 400);
    },

    close(el, key) {
      el.classList.remove('is-open');
      // A null key means "do not remember" — the theme editor passes it so the
      // merchant can open and close the panel as often as styling requires.
      if (key) this.store(key, String(Date.now()));
      const done = () => { el.hidden = true; };
      if (reduceMotion) done();
      else setTimeout(done, 700);
    }
  };

  /* ---- 16 · Collection navigator ---------------------------------------
     Switches curated collections in place. The markup ships with every panel
     visible; this module marks the section ready, which is what allows the CSS
     to reduce it to one panel. If the script never runs the visitor still sees
     all collections — the content is never hidden behind JavaScript. */
  const Navigator = {
    init() {
      $$('[data-navigator]').forEach((root) => {
        if (!claim(root)) return;
        const tabs = $$('[data-navigator-tab]', root);
        const panels = $$('[data-navigator-panel]', root);
        if (tabs.length < 2 || tabs.length !== panels.length) return;

        root.setAttribute('data-ready', '');

        const select = (index, moveFocus) => {
          tabs.forEach((tab, i) => {
            const on = i === index;
            tab.classList.toggle('is-active', on);
            tab.setAttribute('aria-selected', on ? 'true' : 'false');
            tab.tabIndex = on ? 0 : -1;
          });
          panels.forEach((panel, i) => panel.classList.toggle('is-active', i === index));
          if (moveFocus) tabs[index].focus();
        };

        tabs.forEach((tab, i) => on(tab, 'click', () => select(i)));

        /* Ziehen mit der Maus. Touch und Trackpad scrollen die Leiste bereits
           nativ; am Desktop fehlt das Ziehen, und ohne sichtbare Scrollbar
           erkennt dort sonst niemand, dass es weitergeht. Ein Klick auf eine
           Karte darf dabei nicht ausgeloest werden, sobald wirklich gezogen
           wurde — darum die Schwelle von 4 Pixeln. */
        $$('[data-rail]', root).forEach((rail) => {
          let active = false, gezogen = false, startX = 0, startLeft = 0;

          on(rail, 'pointerdown', (e) => {
            if (e.pointerType !== 'mouse' || e.button !== 0) return;
            active = true;
            gezogen = false;
            startX = e.clientX;
            startLeft = rail.scrollLeft;
          });

          on(rail, 'pointermove', (e) => {
            if (!active) return;
            const weg = e.clientX - startX;
            if (!gezogen && Math.abs(weg) < 4) return;
            if (!gezogen) {
              gezogen = true;
              rail.classList.add('is-dragging');
              rail.setPointerCapture(e.pointerId);
            }
            e.preventDefault();
            rail.scrollLeft = startLeft - weg;
          });

          const beenden = (e) => {
            if (!active) return;
            active = false;
            if (!gezogen) return;
            rail.classList.remove('is-dragging');
            if (e && e.pointerId !== undefined && rail.hasPointerCapture(e.pointerId)) {
              rail.releasePointerCapture(e.pointerId);
            }
            // Klick nach dem Ziehen unterdruecken, sonst oeffnet sich ein Produkt.
            on(rail, 'click', (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
          };

          on(rail, 'pointerup', beenden);
          on(rail, 'pointercancel', beenden);
          on(rail, 'pointerleave', beenden);
          on(rail, 'dragstart', (e) => { if (gezogen) e.preventDefault(); });
        });

        // Arrow keys move between tabs, Home/End jump to the ends — the
        // keyboard contract every tablist is expected to honour.
        on(root, 'keydown', (e) => {
          const current = tabs.indexOf(doc.activeElement);
          if (current === -1) return;
          let next = null;
          if (e.key === 'ArrowRight') next = (current + 1) % tabs.length;
          else if (e.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = tabs.length - 1;
          if (next === null) return;
          e.preventDefault();
          select(next, true);
        });
      });
    }
  };

  /* ---- boot -------------------------------------------------------------
     Modules are isolated from one another. A single failing module used to
     abort the whole sequence, which left reveal-animated content stuck at
     opacity 0 and the loading overlay covering the page — a blank shop from
     one exception. Each init now fails alone and the rest still run. */
  const MODULES = [
    ['LoadingScreen', LoadingScreen],
    ['Reveal', Reveal],
    ['SplitText', SplitText],
    ['Header', Header],
    ['NavPanel', NavPanel],
    ['MobileMenu', MobileMenu],
    ['Beschreibung', Beschreibung],
    ['Accordion', Accordion],
    ['Magnetic', Magnetic],
    ['Cursor', Cursor],
    ['Parallax', Parallax],
    ['HeroVideo', HeroVideo],
    ['Marquee', Marquee],
    ['Quantity', Quantity],
    ['Cart', Cart],
    ['Product', Product],
    ['CookiePreferences', CookiePreferences],
    ['WelcomeOffer', WelcomeOffer],
    ['Navigator', Navigator]
  ];

  function boot() {
    // Last-resort safety: whatever happens below, the overlay must not persist.
    const veil = $('[data-loading-screen]');
    if (veil) setTimeout(() => veil.remove(), 4000);

    MODULES.forEach(([name, mod]) => {
      try {
        mod.init();
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('[KIVORA] ' + name + ' konnte nicht initialisiert werden:', err);
        }
      }
    });
  }

  /* Sicherheitsnetz: Sollte der Beobachter aus irgendeinem Grund nicht
     ausloesen, werden die Zeilen nach kurzer Zeit trotzdem sichtbar. Eine
     unsichtbare Schlagzeile ist der teuerste denkbare Fehler. */
  setTimeout(() => {
    $$('[data-split]:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
  }, 1200);

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Theme Editor section load/reorder
  doc.addEventListener('shopify:section:load', boot);
  doc.addEventListener('shopify:section:select', () => Cart.close());

  window.KIVORA = Object.assign(K, { Cart, Product, formatMoney });
})();